import { useLazyQuery, type ApolloClient } from '@apollo/client';
import { exportXlsModal } from 'motion-components';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues } from './filters';
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { isAfisCyprus } from 'shared-pages/common/constants';
import type { TFunction } from 'i18next';

type QueryState = {
  filterValues?: Record<string, unknown>;
};

type WasteReceptionPoint = {
  isActive?: boolean;
  firstDate?: string;
  firstUser?: string;
  lastDate?: string;
  lastUser?: string;
} & Record<string, unknown>;

type GetWasteReceptionPointsResult = {
  getWastereceptionpoints: {
    data: WasteReceptionPoint[];
    recordCount?: number;
  };
};

export const exportColumns = (t: TFunction) => {
  return [
    { fieldName: 'id', fieldTitle: t('ID'), fieldType: 'TEXT' },
    { fieldName: 'name', fieldTitle: t('Επωνυμία'), fieldType: 'TEXT' },
    { fieldName: 'code', fieldTitle: t('Κωδικός'), fieldType: 'TEXT' },
    { fieldName: 'erpId', fieldTitle: t('ERP ΚΩΔΙΚΟΣ'), fieldType: 'TEXT' },
    { fieldName: 'wastedestinationtype.name', fieldTitle: t('Τύπος'), fieldType: 'TEXT' },
    { fieldName: 'addressLine1', fieldTitle: t('Διεύθυνση'), fieldType: 'TEXT' },
    { fieldName: 'addressLine2', fieldTitle: t('Διεύθυνση 2'), fieldType: 'TEXT' },
    { fieldName: 'addressArea', fieldTitle: t('Περιοχή'), fieldType: 'TEXT' },
    { fieldName: 'addressCity', fieldTitle: t('Πόλη'), fieldType: 'TEXT' },
    { fieldName: 'postalCode', fieldTitle: t('Τ.Κ.'), fieldType: 'TEXT' },
    { fieldName: 'phone', fieldTitle: t('Τηλέφωνο'), fieldType: 'TEXT' },
    { fieldName: 'companyVat', fieldTitle: t('Α.Φ.Μ.'), fieldType: 'TEXT' },
    ...(!isAfisCyprus
      ? [{ fieldName: 'companyTaxOffice', fieldTitle: t('Δ.Ο.Υ.'), fieldType: 'TEXT' }]
      : []),

    { fieldName: 'receptionPersonName', fieldTitle: t('Υπεύθυνος'), fieldType: 'TEXT' },
    { fieldName: 'contractNo', fieldTitle: t('Αριθμός Σύμβασης'), fieldType: 'TEXT' },
    {
      fieldName: 'contractSignDate',
      fieldTitle: t('Ημερομηνία Υπογραφής Σύμβασης'),
      fieldType: 'DATE',
    },
    { fieldName: 'contractExpDate', fieldTitle: t('Λήξη Σύμβασης'), fieldType: 'DATE' },
    { fieldName: 'isActive', fieldTitle: t('Ενεργή Εγγραφή'), fieldType: 'TEXT' },
    { fieldName: 'firstRec', fieldTitle: t('Δήλωση'), fieldType: 'TEXT' },
    { fieldName: 'lastRec', fieldTitle: t('Αλλαγή'), fieldType: 'TEXT' },
  ];
};

export async function handleExport(
  client: ApolloClient<object>,
  queryState: QueryState | undefined,
  t: TFunction,
): Promise<void> {
  function useGetQuery() {
    const [get, { loading }] = useLazyQuery<GetWasteReceptionPointsResult>(gqlGetQuery, {
      onCompleted: transformData,
    });
    const [result, setResult] = useState<[typeof get, { loading: boolean; data?: unknown }]>([
      get,
      { loading, data: undefined },
    ]);
    function transformData(data: GetWasteReceptionPointsResult) {
      const recs = data.getWastereceptionpoints.data.map((values) => {
        return {
          ...values,
          isActive: !values.isActive ? t('ΟΧΙ') : t('ΝΑΙ'),
          firstRec: ` ${
            values.firstDate ? format(parseISO(values.firstDate), 'dd/MM/yy HH:mm') : ''
          }  - ${values.firstUser}`,
          lastRec: ` ${
            values.lastDate ? format(parseISO(values.lastDate), 'dd/MM/yy HH:mm') : ''
          }  - ${values.lastUser}`,
        };
      });
      const trsData = {
        getWastereceptionpoints: {
          data: recs,
          recordCount: data?.getWastereceptionpoints?.recordCount,
        },
      };
      setResult([get, { loading, data: trsData }]);
    }
    return result;
  }
  const filterVariables = {
    where: buildWhereFromFilterValues(queryState?.filterValues),
  };
  await exportXlsModal({
    apolloClient: client,
    exportFileName: 'export WasteReceptionPoints',
    getLazyQuery: useGetQuery,
    columnsDef: exportColumns(t),
    filterVariables: filterVariables,
  });
}
